import React from "react";
import { cn } from "../../lib/cn";

// Flat fills, no gradients, no glow, no hover-lift.
// `primary` uses the AA-safe fill token, never the bright brand green.
const VARIANTS = {
  primary: "font-display bg-primary text-onprimary hover:bg-primary-hi",
  ghost: "text-ink ring-1 ring-inset ring-line/20 hover:bg-line/5",
  subtle: "bg-primary/10 text-ink ring-1 ring-inset ring-line/10 hover:bg-primary/15",
  accent: "bg-accent/12 text-ink ring-1 ring-inset ring-accent/25 hover:bg-accent/20",
  danger: "bg-danger text-white hover:opacity-90",
  link: "text-primary hover:text-primary-hi !px-0 !py-0 !shadow-none",
};

// Compact control heights: sm 28px, md 34px, lg 38px.
const SIZES = {
  sm: "text-xs px-2.5 py-1.5 gap-1.5",
  md: "text-sm px-3.5 py-2 gap-1.5",
  lg: "text-sm px-4 py-2.5 gap-2",
};

/**
 * Primary action primitive. Token-driven, dark-aware.
 * Props: variant (primary|ghost|subtle|accent|danger|link), size (sm|md|lg),
 *        as ("button"|"a"|Component), leftIcon, rightIcon, loading, fullWidth.
 */
export const Button = React.forwardRef(function Button(
  {
    as: Comp = "button",
    variant = "primary",
    size = "md",
    leftIcon,
    rightIcon,
    loading = false,
    fullWidth = false,
    className,
    children,
    disabled,
    ...props
  },
  ref
) {
  return (
    <Comp
      ref={ref}
      disabled={Comp === "button" ? disabled || loading : undefined}
      className={cn(
        // kbc-control marks this as a real control so the touch-target rules in
        // index.css can grow its hit area, whichever element it renders as
        // (button, a, Link).
        "kbc-control group inline-flex items-center justify-center whitespace-nowrap rounded-md font-semibold leading-none transition-colors duration-150 ease-smooth", "focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
        VARIANTS[variant],
        SIZES[size],
        fullWidth && "w-full",
        className
      )}
      {...props}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
          <path className="opacity-90" d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )}
      {!loading && leftIcon}
      {children}
      {rightIcon}
    </Comp>
  );
});

export default Button;
